<template>
  <div>
    <!--
      Two drawers, one menu.

      Toggling `permanent`/`temporary` on a single drawer meant its geometry and
      background were decided by bound props, and a miss there left the panel
      open but unpainted - white nav text on a white page, with only Vuetify's
      shadow to show for it. These are the two configurations that are each
      known to work, picked by breakpoint, both fed the same `items` prop, so
      the phone menu still cannot drift from the desktop one.

      The gradient is inline rather than in the stylesheet: Vuetify themes the
      drawer white, and an inline style cannot be lost to scoping or specificity.
    -->

    <!-- desktop: pinned open, from md up -->
    <v-navigation-drawer
      class="fixedSidebar hidden-sm-and-down"
      :style="surface"
      clipped
      width="280"
      height="100vh"
      permanent
      fixed
    >
      <div class="sidebar-header">
        <v-img
          contain
          width="90%"
          height="40px"
          src="../../assets/sarpaodisha_1.png"
          class="sidebar-logo"
        ></v-img>
      </div>
      <v-list dense nav class="px-2 pt-4">
        <v-list-item
          v-for="item in items"
          :key="item.title"
          link
          exact
          :to="item.path"
          class="nav-item mb-2"
          active-class="active-nav-item"
        >
          <v-list-item-icon class="mr-3">
            <v-icon class="nav-icon">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="nav-title">{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- mobile: slides over the page, below md -->
    <v-navigation-drawer
      class="fixedSidebar mobileSidebar hidden-md-and-up"
      :style="surface"
      :value="value"
      @input="$emit('input', $event)"
      width="280"
      temporary
      fixed
    >
      <div class="sidebar-header">
        <v-img
          contain
          width="90%"
          height="40px"
          src="../../assets/sarpaodisha_1.png"
          class="sidebar-logo"
        ></v-img>
      </div>
      <v-list dense nav class="px-2 pt-4">
        <v-list-item
          v-for="item in items"
          :key="item.title"
          link
          exact
          :to="item.path"
          class="nav-item mb-2"
          active-class="active-nav-item"
        >
          <v-list-item-icon class="mr-3">
            <v-icon class="nav-icon">{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="nav-title">{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script>
/*
 * The portal's navigation sidebar, shared by every role layout.
 *
 * Each layout passes its own `items`; v-model carries the open state, which
 * only matters below md where the drawer slides over the page.
 */
const SURFACE = "linear-gradient(180deg, #162a1c 0%, #0e1b12 100%)";

export default {
  name: "AppSidebar",
  props: {
    items: {
      type: Array,
      default: function () {
        return [];
      },
    },
    //v-model: whether the mobile drawer is open
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    surface() {
      return { background: SURFACE };
    },
  },
};
</script>

<style scoped>
/* the drawer's inner layer is what actually fills it, so it has to carry the
   gradient too or the theme's white shows through */
.fixedSidebar ::v-deep .v-navigation-drawer__content {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  overflow: hidden !important;
  background: linear-gradient(180deg, #162a1c 0%, #0e1b12 100%) !important;
}
.fixedSidebar {
  border-right: none !important;
  /* `clipped` drops Vuetify's stacking to 4, which puts the app bar over it;
     the layouts used to raise it here and the desktop drawer still needs that */
  z-index: 10;
}
/* clear the app bar, the page and Vuetify's own scrim */
.mobileSidebar {
  z-index: 1000 !important;
}

/* make the list scroll on its own, under a fixed header */
.fixedSidebar ::v-deep .v-list {
  flex: 1 !important;
  overflow-y: auto !important;
  padding-bottom: 24px !important;
}
.fixedSidebar ::v-deep .v-list::-webkit-scrollbar {
  width: 5px;
}
.fixedSidebar ::v-deep .v-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.02);
}
.fixedSidebar ::v-deep .v-list::-webkit-scrollbar-thumb {
  background: rgba(16, 185, 129, 0.3);
  border-radius: 4px;
}
.fixedSidebar ::v-deep .v-list::-webkit-scrollbar-thumb:hover {
  background: rgba(16, 185, 129, 0.6);
}

.sidebar-header {
  background: #fafafa !important; /* matches the app header strip */
  height: 60px !important;
  min-height: 60px !important;
  max-height: 60px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  box-sizing: border-box !important;
}
.sidebar-logo {
  max-height: 40px !important;
  filter: none !important;
}

.nav-item {
  position: relative;
  border-radius: 12px !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
  margin-bottom: 8px !important;
  padding: 0 16px !important;
  overflow: hidden;
  letter-spacing: 0.43px;
}

/* Sliding Active Indicator */
.nav-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 10%;
  height: 80%;
  width: 4px;
  background: #10b981;
  border-radius: 0 4px 4px 0;
  transform: scaleY(0);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2;
}

.nav-item:hover {
  background: rgba(52, 211, 153, 0.12) !important;
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.nav-icon {
  color: #a7f3d0 !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.nav-title {
  color: #e6f4ea !important;
  font-size: 14px;
  font-family: poppinsmedium;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.nav-item:hover .nav-icon,
.nav-item:hover .nav-title {
  color: #34d399 !important;
}

/* Active State Highlighting */
.active-nav-item {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.25),
    0 4px 8px rgba(16, 185, 129, 0.15) !important;
  transform: translateX(6px);
}
.active-nav-item::before {
  transform: scaleY(1);
  background: #ffffff; /* Contrast on active item */
}
.active-nav-item .nav-icon,
.active-nav-item .nav-title {
  color: #ffffff !important;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>
